import AddIcon from "@mui/icons-material/Add";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import { Button, Chip, Typography } from "@mui/material";
import { GridActionsCellItem, GridColDef, GridRowParams } from "@mui/x-data-grid-pro";
import moment from "moment";
import { useContext, useEffect, useState } from "react";
import { ModalContext } from "../../../Components/Modals";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../../Components/Modals/ModalConfirmAction/modal-confirm-action";
import ModalCreateOrUpdateDocument, {
  ModalCreateOrUpdateDocumentElementProps,
} from "../../../Components/Modals/ModalCreateOrUpdateDocument/modal-create-or-update-document";
import DataTable, { CellSize } from "../../../Components/Table/table";
import TableSkeleton from "../../../Components/Table/TableSkeleton";
import { cacheService, fileService, setupService } from "../../../Core/Api/services";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { UploadCardFormatDto } from "../../../Core/Api/Types/db-format-infos";
import { PlatformDocument } from "../../../Core/Api/Types/platform-document";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { OpenLinkInNewTab, UserHasAcl } from "../../../Utils/helpers";
import { useTranslation } from "react-i18next";
import { DeleteOutlineOutlined, EditOutlined } from "@mui/icons-material";

const SupportDocuments = () => {
  const { t } = useTranslation();
  const [rows, setRows] = useState<PlatformDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const authKey = cacheService.GetAuthKey();
  const [format, setFormat] = useState<UploadCardFormatDto | null>(null);

  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  const userCanUploadAndDeleteDocs = UserHasAcl(AclTypes.aclPlatformDocumentsUploadAndDelete);

  const handleAddClick = () => {
    openModal &&
      openModal(ModalCreateOrUpdateDocument, {
        format: format!,
      });
  };

  const handleUpdateClick = (document: PlatformDocument) => {
    openModal &&
      openModal<ModalCreateOrUpdateDocumentElementProps>(ModalCreateOrUpdateDocument, {
        document,
        format: format!,
      });
  };

  const handleDeleteClick = (document: PlatformDocument) => {
    openModal &&
      openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
        title: t("modal:title.confirmDocumentDeletion"),
        onConfirm: () => {
          fileService.DeletePlatformDocument(document.id).then((response) => {
            if (response.success) {
              setRows(rows.filter((r) => r.id !== document.id));
            }
          });
        },
      });
  };

  // Cols def
  const cols: GridColDef[] = [
    {
      field: "type",
      headerName: t("common:type"),
      hideable: true,
      flex: CellSize.large,
      minWidth: CellSize.large,
      renderCell: (data: any) => <Chip label={data.value} />,
    },
    {
      field: "date",
      headerName: t("common:date"),
      flex: CellSize.medium,
      minWidth: CellSize.medium,
      renderCell: (data: any) => (
        <Typography component="span" fontWeight={500} color="text.primary">
          {moment(data.value).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
        </Typography>
      ),
    },
    {
      field: "description",
      headerName: t("common:description"),
      flex: CellSize.xxlarge,
      minWidth: CellSize.xxlarge,
      renderCell: (data: any) => (
        <Typography component="span" fontWeight={500} color="text.primary">
          {data.value}
        </Typography>
      ),
    },
    {
      field: "version",
      headerName: t("common:version"),
      flex: CellSize.xsmall,
      minWidth: CellSize.xsmall,
      renderCell: (data: any) => <Typography variant="body1">{data.value}</Typography>,
    },
    {
      field: "fileName",
      headerName: t("table:headerName.fileName"),
      flex: CellSize.xxlarge,
      minWidth: CellSize.xxlarge,
      renderCell: (data: any) => <Typography variant="body1">{data.value}</Typography>,
    },
    {
      field: "actions",
      type: "actions",
      align: "right",
      minWidth: CellSize.actionsDefault,
      flex: CellSize.actionsDefault,
      getActions: (params: GridRowParams) => {
        if (!params || !params.row) return [];
        const output = [
          <GridActionsCellItem
            icon={<CloudDownloadIcon sx={{ width: 20, height: 20, color: "text.primary" }} />}
            label={t("common:download")}
            onClick={() => {
              OpenLinkInNewTab(fileService.DownloadPlatformDocument(params?.row?.id, authKey!));
            }}
          />,
        ];

        if (userCanUploadAndDeleteDocs)
          output.push(
            ...[
              <GridActionsCellItem
                icon={<EditOutlined sx={{ width: 20, height: 20, color: "primary.text" }} />}
                label={t("common:editUpperCase")}
                onClick={() => handleUpdateClick(params.row as PlatformDocument)}
              />,
              <GridActionsCellItem
                icon={<DeleteOutlineOutlined sx={{ width: 20, height: 20, color: "error.main" }} />}
                label={t("common:delete")}
                onClick={() => handleDeleteClick(params.row as PlatformDocument)}
              />,
            ]
          );

        return output;
      },
    },
  ];

  useEffect(() => {
    fileService.GetPlatformDocuments().then((response) => {
      if (response.documents) setRows(response.documents);

      setLoading(false);
    });
    setupService.GetFormats().then((response) => {
      const docFormat = response.formats?.find((f) => f.id === 33 || f.name === "Document File");
      if (!docFormat) return;
      docFormat.maxFileSizeMB = 5; // [LB] Not set in db, value come from the backend

      const formatDto = setupService.MapDbFormatInfosToUploadCardFormatDto([docFormat])[0];
      setFormat(formatDto);
    });
  }, []);

  const additionalActions = (
    <>
      {userCanUploadAndDeleteDocs && (
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddClick}>
          {t("button:addADocument")}
        </Button>
      )}
    </>
  );

  return (
    <>
      {loading ? (
        <TableSkeleton />
      ) : (
        <DataTable
          rows={rows}
          columns={cols}
          additionalActions={additionalActions}
          columnHeaderHeight={48}
          rowHeight={56}
          getRowHeight={() => "auto"}
          checkboxSelection={false}
          disableRowSelectionOnClick
          disableColumnFilter
          pagination={false}
        />
      )}
    </>
  );
};

export default SupportDocuments;
